// Lead Generation System — staff Lead Generator roster. Managers invite, edit,
// suspend/reactivate, and archive the external contractors who source preleads.
// A Lead Generator is a portal account, never a broker or a Phoenix staff user.
// Read gates on LeadGen.View; every mutation gates on LeadGen.Manage.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Badge, Button, Input, OverflowMenu, useToast } from "@/design-system/components/ui";
import type { OverflowMenuItem } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { displayEmail, formatPhone } from "@/design-system/lib/format";
import type {
  LeadGeneratorListQuery,
  LeadGeneratorStatus,
  LeadGeneratorSummaryDto,
} from "@/api/leadGen/leadGenerators";
import {
  useArchiveLeadGenerator,
  useInviteLeadGenerator,
  useLeadGenerators,
  useLeadGeneratorStats,
  useSetLeadGeneratorStatus,
} from "@/hooks/leadGen/useLeadGenerators";
import { GENERATOR_STATUS_BADGE, GENERATOR_STATUS_LABEL } from "./leadGenShared";
import { GeneratorFormModal } from "./GeneratorFormModal";

const PAGE_SIZE = 25;
const STATUSES: LeadGeneratorStatus[] = ["Invited", "Active", "Suspended", "Inactive"];

export function GeneratorsRosterPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("LeadGen.Manage");
  const toast = useToast();
  const { date } = useDateFormatter();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<LeadGeneratorStatus | "" | "Archived">("");
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState<{ id?: string } | null>(null);

  const archivedView = status === "Archived";
  const statusParam: LeadGeneratorStatus | undefined =
    status === "" || status === "Archived" ? undefined : status;

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const listParams: LeadGeneratorListQuery = {
    page,
    pageSize: PAGE_SIZE,
    q: search || undefined,
    status: statusParam,
    archived: archivedView || undefined,
  };
  const list = useLeadGenerators(listParams);
  const statsQuery = useLeadGeneratorStats({ q: search || undefined });
  const byStatus = statsQuery.data?.byStatus;

  const invite = useInviteLeadGenerator();
  const setGenStatus = useSetLeadGeneratorStatus();
  const archive = useArchiveLeadGenerator();

  const onError = (err: unknown) => toast.error(extractErrorMessage(err));

  const doInvite = (id: string) =>
    invite.mutate(id, {
      onSuccess: (res) =>
        toast.success(res.isExistingAccount ? "Invitation re-sent to existing account" : `Invitation sent to ${res.email}`),
      onError,
    });
  const doSetStatus = (id: string, next: LeadGeneratorStatus) =>
    setGenStatus.mutate({ id, status: next }, { onSuccess: () => toast.success("Status updated"), onError });
  const doArchive = (id: string, archived: boolean) =>
    archive.mutate(
      { id, archived },
      { onSuccess: () => toast.success(archived ? "Generator unarchived" : "Generator archived"), onError },
    );

  const totalPages = useMemo(() => {
    const data = list.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [list.data]);

  const columns: Column<LeadGeneratorSummaryDto>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        render: (g) => (
          <Link to={`/lead-generation/generators/${g.id}`} className="font-semibold text-onyx hover:text-gold-shadow">
            {g.displayName}
          </Link>
        ),
      },
      {
        key: "email",
        header: "Email",
        render: (g) => <span className="text-small text-gray-mid">{displayEmail(g.email) ?? g.email}</span>,
      },
      {
        key: "phone",
        header: "Phone",
        mobileHidden: true,
        render: (g) => <span className="font-mono text-caption">{formatPhone(g.phone) || "—"}</span>,
      },
      {
        key: "status",
        header: "Status",
        render: (g) => (
          <div className="flex items-center gap-space-2">
            <Badge status={GENERATOR_STATUS_BADGE[g.status]}>{GENERATOR_STATUS_LABEL[g.status]}</Badge>
            {g.isArchived && <Badge status="archived">Archived</Badge>}
            {g.hasCustomPlan && <Badge status="gold">Custom plan</Badge>}
          </div>
        ),
      },
      {
        key: "portal",
        header: "Portal login",
        mobileHidden: true,
        render: (g) =>
          g.portalUserId ? (
            <span className="text-caption text-status-success">Linked</span>
          ) : (
            <span className="text-caption text-gray-mid">Not invited</span>
          ),
      },
      {
        key: "hired",
        header: "Hired",
        mobileHidden: true,
        render: (g) => <span className="text-caption">{g.hiredAtUtc ? date(g.hiredAtUtc) : "—"}</span>,
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (g) =>
          canManage ? <RowActions g={g} onInvite={doInvite} onSetStatus={doSetStatus} onArchive={doArchive} onEdit={(id) => setFormOpen({ id })} /> : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, date],
  );

  return (
    <>
      <PageHeader
        eyebrow="Lead Generation"
        title="Lead Generators"
        description="The external contractors who source preleads. Invite their portal login, monitor status, and manage the roster. Phoenix is the Loan Administrator — generators are contractors, not brokers or staff."
        actions={
          canManage ? (
            <Button size="sm" onClick={() => setFormOpen({})}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New generator
            </Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-space-3 mb-space-4">
        <StatCard label="Total" value={statsQuery.data?.total ?? 0} />
        <StatCard label="Invited" value={byStatus?.Invited ?? 0} />
        <StatCard label="Active" value={byStatus?.Active ?? 0} />
        <StatCard label="Suspended" value={byStatus?.Suspended ?? 0} />
        <StatCard label="Inactive" value={byStatus?.Inactive ?? 0} />
      </div>

      <div className="flex flex-wrap items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="grow"
        />
        <select
          className="h-10 w-44 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={status}
          onChange={(e) => setStatus(e.target.value as LeadGeneratorStatus | "" | "Archived")}
          aria-label="Status filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {GENERATOR_STATUS_LABEL[s]}
            </option>
          ))}
          <option value="Archived">Archived</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={list.data?.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No lead generators match your filters."
        rowKey={(g) => g.id}
      />

      {list.data && list.data.totalCount > 0 && (
        <Pagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          totalCount={list.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}

      {formOpen && <GeneratorFormModal generatorId={formOpen.id} onClose={() => setFormOpen(null)} />}
    </>
  );
}

function RowActions({
  g,
  onInvite,
  onSetStatus,
  onArchive,
  onEdit,
}: {
  g: LeadGeneratorSummaryDto;
  onInvite: (id: string) => void;
  onSetStatus: (id: string, next: LeadGeneratorStatus) => void;
  onArchive: (id: string, archived: boolean) => void;
  onEdit: (id: string) => void;
}) {
  const items: OverflowMenuItem[] = [
    { label: g.portalUserId ? "Re-send portal invite" : "Send portal invite", onSelect: () => onInvite(g.id) },
    { label: "Edit", onSelect: () => onEdit(g.id) },
  ];
  if (g.status === "Active") {
    items.push({ label: "Suspend", onSelect: () => onSetStatus(g.id, "Suspended") });
  } else if (g.status === "Suspended" || g.status === "Inactive" || g.status === "Invited") {
    // Invited included as a manual safety net: activation normally fires
    // automatically when the generator redeems their portal invite.
    items.push({ label: g.status === "Invited" ? "Activate" : "Reactivate", onSelect: () => onSetStatus(g.id, "Active") });
  }
  items.push({
    label: g.isArchived ? "Unarchive" : "Archive",
    destructive: !g.isArchived,
    confirm: g.isArchived ? undefined : "Archive this generator?",
    onSelect: () => onArchive(g.id, g.isArchived),
  });
  return <OverflowMenu items={items} label={`Actions for ${g.displayName}`} />;
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="border border-border-light rounded-md p-space-3 bg-white-pure">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="font-display text-h3 text-onyx">{value}</div>
    </div>
  );
}
